import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { ChevronRight, PlaceOutlined, ScheduleOutlined, SportsOutlined } from '@mui/icons-material';
import { HotGame, getHotGames } from '../../../core/API/hotGames';
import { useAutoScroll } from './useAutoScroll';
import './HotGames.scss';

interface HotGamesProps {
	title?: string;
	onGamesLoaded?: (games: HotGame[]) => void;
	onGameClick?: (game: HotGame) => void;
	/** Slowly scroll the list when it overflows. Defaults to true. */
	autoScroll?: boolean;
}

const HotGames: React.FC<HotGamesProps> = ({
	title = 'Mabel A.I. - Hot Games',
	onGamesLoaded,
	onGameClick,
	autoScroll = true,
}) => {
	const [games, setGames] = useState<HotGame[]>([]);
	const [loading, setLoading] = useState(true);
	const isClickable = Boolean(onGameClick);
	const listRef = useRef<HTMLDivElement>(null);

	useAutoScroll(listRef, { enabled: autoScroll && !loading && games.length > 1 });

	useEffect(() => {
		const fetchGames = async () => {
			try {
				const hotGamesData = await getHotGames();
				setGames(hotGamesData);
				if (onGamesLoaded) {
					onGamesLoaded(hotGamesData);
				}
			} catch (error) {
				console.error('Error fetching hot games:', error);
				setGames([]);
			} finally {
				setLoading(false);
			}
		};

		fetchGames();
	}, [onGamesLoaded]);

	return (
		<Grid container direction="column" className="hotGamesCard hot-games-container white-card">
			<Grid item className="hot-games-header">
				<Typography variant="h6" sx={{ py: 2, m: 0, fontWeight: 500, fontSize: '20px' }}>
					{title}
				</Typography>
			</Grid>
			{loading ? (
				<Grid item className="loading-container">
					<CircularProgress size={20} />
				</Grid>
			) : (
				<Grid item ref={listRef} className="hot-games-list scrollable">
					{games.map((game) => (
						<Grid
							key={game.id}
							container
							item
							className={`hot-game-item${isClickable ? ' is-clickable' : ''}`}
							direction="row"
							wrap="nowrap"
							onClick={() => onGameClick?.(game)}
							role={isClickable ? 'button' : undefined}
							tabIndex={isClickable ? 0 : undefined}
							onKeyDown={(e) => {
								if (!isClickable) return;
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									onGameClick?.(game);
								}
							}}
						>
							<div className="game-icon-badge" aria-hidden>
								<SportsOutlined fontSize="small" />
							</div>
							<div className="game-content">
								<div className="game-title">{game.teams}</div>
								<div className="game-meta">
									<ScheduleOutlined className="meta-icon" fontSize="inherit" />
									<span>
										{game.date?.slice(0, 10)} {game.time} {game.timezone}
									</span>
								</div>
								{game.why_hot && <div className="hot-reason">{game.why_hot}</div>}
								{game.broadcast && <div className="broadcast">{game.broadcast}</div>}
								{(game.city || game.country) && (
									<div className="game-location">
										<PlaceOutlined className="meta-icon" fontSize="inherit" />
										<span>
											{game.city}
											{game.city && game.country ? ', ' : ''}
											{game.country}
										</span>
									</div>
								)}
							</div>
							{isClickable && (
								<div className="game-chevron" aria-hidden>
									<ChevronRight fontSize="small" />
								</div>
							)}
						</Grid>
					))}
					{games.length === 0 && !loading && (
						<Grid item className="no-games">
							No hot games available
						</Grid>
					)}
				</Grid>
			)}
		</Grid>
	);
};

export default HotGames;
